import { useState, useEffect, useMemo, useRef } from "react";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { partitionByActive, type TimeWindow } from "../../utils/entityActive";
import { storedEntityTypes, withCurrentType } from "../../types";
import type { Entity, EntityType } from "../../types";

type EntityWithId = Entity & { id: string };

interface EntityPickerProps {
  /** Which entity types to show in the picker */
  types: EntityType[];
  /** Currently selected entity ID */
  value: string | null;
  /** Called when selection changes */
  onChange: (entityId: string | null, entity: EntityWithId | null) => void;
  /** Placeholder text */
  placeholder?: string;
  /** If provided, restrict choices to entities whose `parentId` matches.
   *  `undefined` means "no parent filter"; `null` matches top-level entities. */
  parentId?: string | null;
  /** Optional message shown in the dropdown when there are no matches. */
  emptyHint?: string;
  /**
   * The stretch of time to judge "is this still a place" by — from
   * `useActiveAsOf`. Entities outside it drop out of the browse list and
   * gather in a "No longer running" group once you search by name, which is
   * what makes backfilling an old trip still possible.
   *
   * Opt-in on purpose: filing a newly transcribed house under last year's
   * event means picking a retired parent, so the forms leave this unset.
   */
  activeDuring?: TimeWindow;
}

function typeLabel(type: string): string {
  return type.replace(/_/g, " ");
}

function useEntitiesByTypes(types: EntityType[]) {
  const [entities, setEntities] = useState<EntityWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const typesKey = types.slice().sort().join(",");

  useEffect(() => {
    if (types.length === 0) {
      setEntities([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "entities"),
      where("type", "in", storedEntityTypes(types)),
      orderBy("displayName", "asc"),
    );
    const unsub = onSnapshot(q, (snap) => {
      setEntities(snap.docs.map((d) => withCurrentType({ id: d.id, ...d.data() } as EntityWithId)));
      setLoading(false);
    });
    return unsub;
    // `types` is a fresh array each render, so depending on it would tear
    // down and re-open the snapshot listener constantly. `typesKey` is its
    // stable serialisation and is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesKey]);

  return { entities, loading };
}

export function EntityPicker({ types, value, onChange, placeholder = "Select entity...", parentId, emptyHint, activeDuring }: EntityPickerProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { entities: allEntities, loading } = useEntitiesByTypes(types);
  const entities = useMemo(
    () => (parentId === undefined ? allEntities : allEntities.filter((e) => (e.parentId ?? null) === parentId)),
    [allEntities, parentId],
  );

  const selectedName = useMemo(() => {
    if (!value) return null;
    return entities.find((e) => e.id === value)?.displayName ?? null;
  }, [value, entities]);

  const { grouped, retired } = useMemo(() => {
    const s = search.toLowerCase().trim();
    const matching = s
      ? entities.filter((e) => e.displayName.toLowerCase().includes(s))
      : entities;

    // Retired places are never in the browse list, but a name you deliberately
    // typed comes back — silently finding nothing for a place you know exists
    // is worse than showing it under a label that explains itself.
    const { active, retired: out } = activeDuring
      ? partitionByActive(matching, activeDuring)
      : { active: matching, retired: [] as EntityWithId[] };

    const groups: { type: EntityType; entities: EntityWithId[] }[] = [];
    const typeMap = new Map<EntityType, EntityWithId[]>();
    for (const e of active) {
      const list = typeMap.get(e.type) ?? [];
      list.push(e);
      typeMap.set(e.type, list);
    }
    for (const t of types) {
      const list = typeMap.get(t);
      if (list?.length) groups.push({ type: t, entities: list });
    }
    return { grouped: groups, retired: s ? out : [] };
  }, [entities, search, types, activeDuring]);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  return (
    <div className="entity-picker" ref={ref}>
      <div
        className="entity-picker-trigger form-input"
        onClick={() => setOpen(!open)}
      >
        {value ? (
          <span className="entity-picker-value">
            {selectedName ?? "Loading..."}
            <button
              type="button"
              className="entity-picker-clear"
              onClick={(e) => { e.stopPropagation(); onChange(null, null); setOpen(false); }}
              aria-label="Clear selection"
            >
              &times;
            </button>
          </span>
        ) : (
          <span className="text-muted">{placeholder}</span>
        )}
      </div>
      {open && (
        <div className="entity-picker-dropdown">
          <input
            type="text"
            className="entity-picker-search"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="entity-picker-list">
            {loading ? (
              <p className="entity-picker-empty">Loading...</p>
            ) : grouped.length === 0 && retired.length === 0 ? (
              <p className="entity-picker-empty">{emptyHint ?? "No matches"}</p>
            ) : (
              grouped.map((group) => (
                <div key={group.type} className="entity-picker-group">
                  {types.length > 1 && (
                    <div className="entity-picker-group-label">{typeLabel(group.type)}s</div>
                  )}
                  {group.entities.map((ent) => (
                    <button
                      key={ent.id}
                      type="button"
                      className={`entity-picker-option${ent.id === value ? " entity-picker-option-selected" : ""}`}
                      onClick={() => { onChange(ent.id, ent); setOpen(false); setSearch(""); }}
                    >
                      {ent.displayName}
                    </button>
                  ))}
                </div>
              ))
            )}
            {!loading && retired.length > 0 && (
              <div className="entity-picker-group entity-picker-group-retired">
                <div className="entity-picker-group-label">No longer running</div>
                {retired.map((ent) => (
                  <button
                    key={ent.id}
                    type="button"
                    className={`entity-picker-option entity-picker-option-retired${ent.id === value ? " entity-picker-option-selected" : ""}`}
                    onClick={() => { onChange(ent.id, ent); setOpen(false); setSearch(""); }}
                  >
                    {ent.displayName}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
